import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { PmDialogComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  AnalysisConfigurationSummary,
  formatSummaryMonths,
  SUMMARY_ANALYSIS_TYPE_KEYS,
  SUMMARY_CALCULATION_LEVEL_KEYS,
  SUMMARY_EMPTY_VALUE,
  SUMMARY_HIERARCHY_SEPARATOR,
  SUMMARY_LIST_SEPARATOR,
  SUMMARY_MISSING_VALUE_NOTE_KEYS,
  SummaryRow,
} from 'demand-forecasting/analysis/components/analysis-configuration-summary/analysis-configuration-summary.model';
import { AnalysisConfigurationSummaryComponent } from 'demand-forecasting/analysis/components/analysis-configuration-summary/analysis-configuration-summary.component';
import { AnalysisContextService } from 'demand-forecasting/analysis/services/analysis-context.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import {
  createMeasureVisibility,
  getSelectedMeasureColumns,
} from 'demand-forecasting/analysis/services/measure-display';

@Component({
  selector: 'pm-analysis-configuration-details-dialog',
  templateUrl: './analysis-configuration-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PmDialogComponent, AnalysisConfigurationSummaryComponent],
})
export class AnalysisConfigurationDetailsDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<AnalysisConfigurationDetailsDialogComponent>,
  );
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly analysisStateService = inject(AnalysisStateService);
  private readonly analysisContextService = inject(AnalysisContextService);

  private readonly isMeasureVisible = createMeasureVisibility(this.store);

  protected readonly configuration =
    this.analysisStateService.selectedConfiguration;
  private readonly version = this.analysisStateService.selectedVersion;

  protected readonly primaryCtaButtonConfig = {
    text: 'UTILITY.CLOSE',
    handleClick: () => this.close(),
  };

  private readonly generalRows = computed((): SummaryRow[] => {
    const configuration = this.configuration();
    const version = this.version();
    if (!configuration || !version) {
      return [];
    }

    const rows: SummaryRow[] = [
      { label: 'ANALYSIS.DETAILS.NAME', value: configuration.name },
      {
        label: 'ANALYSIS.DETAILS.ANALYSIS_TYPE',
        value: this.translate.instant(
          SUMMARY_ANALYSIS_TYPE_KEYS[configuration.analysisType],
        ),
      },
      {
        label: 'ANALYSIS.TIME_PERIOD',
        value: this.analysisContextService.timePeriodRange(),
      },
    ];

    if (configuration.analysisType === AnalysisType.ROLLING) {
      rows.push(
        {
          label: 'ANALYSIS.CONTEXT_FREEZE_PERIOD',
          value: formatSummaryMonths(this.translate, version.minHorizon),
        },
        {
          label: 'ANALYSIS.DETAILS.LOOKBACK_PERIOD',
          value: formatSummaryMonths(
            this.translate,
            configuration.lookbackPeriod,
          ),
        },
      );
      return rows;
    }

    if (configuration.anchorConsensusCycleId !== null) {
      rows.push({
        label: 'ANALYSIS.CYCLE',
        value: this.translate.instant('ANALYSIS.CYCLE_VALUE', {
          name: configuration.anchorConsensusCycleName ?? SUMMARY_EMPTY_VALUE,
          date: this.dateUtilsService.formatDateBasedOnLanguage(
            configuration.anchorConsensusCycleFinishedAt ?? undefined,
          ),
        }),
      });
    }
    if (configuration.anchorForecastVersionId !== null) {
      rows.push({
        label: 'ANALYSIS.FORECAST_VERSION',
        value:
          this.dateUtilsService.formatDateBasedOnLanguage(
            configuration.anchorForecastVersionCreatedAt ?? undefined,
          ) || SUMMARY_EMPTY_VALUE,
      });
    }
    rows.push({
      label: 'ANALYSIS.DETAILS.HORIZON',
      value: this.analysisContextService.horizon() ?? SUMMARY_EMPTY_VALUE,
    });
    return rows;
  });

  private readonly timeseriesRows = computed((): SummaryRow[] => [
    {
      label: 'ANALYSIS.DETAILS.TIMESERIES_COMPARED',
      value: [
        this.analysisContextService.predictionSeriesName(),
        ...this.analysisContextService.comparisonSeriesNames(),
      ].join(SUMMARY_LIST_SEPARATOR),
    },
  ]);

  private readonly missingValueNoteKey = computed(() => {
    const configuration = this.configuration();
    if (configuration?.analysisType !== AnalysisType.ROLLING) {
      return undefined;
    }
    return SUMMARY_MISSING_VALUE_NOTE_KEYS[configuration.missingValueStrategy];
  });

  private readonly hierarchyRows = computed((): SummaryRow[] => [
    {
      label: 'ANALYSIS.DETAILS.LEVELS',
      value:
        this.configuration()?.hierarchy.join(SUMMARY_HIERARCHY_SEPARATOR) ||
        SUMMARY_EMPTY_VALUE,
    },
  ]);

  private readonly consensusEvaluated = computed(() => {
    const configuration = this.configuration();
    return (
      configuration?.anchorConsensusCycleId != null ||
      configuration?.includeConsensus === true
    );
  });

  private readonly measureRows = computed((): SummaryRow[] => {
    const version = this.version();
    if (!version) {
      return [];
    }
    const measures = getSelectedMeasureColumns(
      this.configuration(),
      this.isMeasureVisible,
      this.translate.instant('ANALYSIS.ACCURACY_SUFFIX'),
    ).map((column) => column.header);

    return [
      {
        label: 'ANALYSIS.DETAILS.ERROR_MEASURES',
        value: measures.join(SUMMARY_LIST_SEPARATOR) || SUMMARY_EMPTY_VALUE,
      },
      {
        label: 'ANALYSIS.DETAILS.CALCULATION_LEVEL',
        value: this.translate.instant(
          SUMMARY_CALCULATION_LEVEL_KEYS[version.calculationLevel],
        ),
      },
    ];
  });

  protected readonly summary = computed(
    (): AnalysisConfigurationSummary => ({
      general: this.generalRows(),
      timeseries: this.timeseriesRows(),
      missingValueNoteKey: this.missingValueNoteKey(),
      hierarchy: this.hierarchyRows(),
      measures: this.measureRows(),
      consensusEvaluated: this.consensusEvaluated(),
    }),
  );

  protected close(): void {
    this.dialogRef.close();
  }
}
